/**
 * Enhanced chat-based search interface with grounded answers and citations.
 *
 * This component provides an intuitive chat interface for natural language
 * search that returns AI-generated answers with inline citations to
 * highlights and articles.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { answerSearch, createAnswerSearchSession, clearAnswerSearchSession } from '../services/search';
import { useToast } from '../hooks/useToast';

const ChatSearch = React.forwardRef(({ onArticleClick, savedSearchState, onSearchStateCleared, className = '' }, ref) => {
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasRestoredStateRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useToast();

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize session and focus input on mount
  useEffect(() => {
    if (savedSearchState && !hasRestoredStateRef.current) {
      // Restore saved search state once - convert timestamp strings back to Date objects
      const messagesWithDates = (savedSearchState.searchMessages || []).map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
      setMessages(messagesWithDates);
      setSessionId(savedSearchState.searchSessionId || null);
      hasRestoredStateRef.current = true;
      if (onSearchStateCleared) {
        onSearchStateCleared();
      }
    } else if (!savedSearchState && !hasRestoredStateRef.current) {
      // Show welcome message immediately
      setMessages([{
        id: 1,
        type: 'system',
        content: 'Hi! Ask me anything about your articles and highlights.',
        timestamp: new Date()
      }]);

      // Initialize session in background
      initializeSession();
    }

    // Focus input after modal opens
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, []); // Empty dependency array - only run once on mount

  const initializeSession = async () => {
    try {
      const session = await createAnswerSearchSession();
      setSessionId(session.session_id);
    } catch (error) {
      console.error('Failed to initialize session:', error);
      showError('Failed to initialize chat session');
    }
  };

  // Build conversation history from messages for follow-up context
  const buildConversationHistory = () => {
    const history = [];
    for (const msg of messages) {
      if (msg.type === 'user') {
        history.push({ role: 'user', content: msg.content });
      } else if (msg.type === 'system' && msg.answerResponse) {
        history.push({ role: 'assistant', content: msg.answerResponse.answer });
      }
    }
    return history.length > 0 ? history : null;
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!query.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: query.trim(),
      timestamp: new Date()
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    const currentQuery = query.trim();
    setQuery('');
    setIsLoading(true);

    try {
      // Build conversation history for follow-ups
      const conversationHistory = buildConversationHistory();

      // Perform answer search
      const response = await answerSearch(currentQuery, {
        sessionId,
        conversationHistory
      });

      // Create system response with the answer
      const systemMessage = {
        id: Date.now() + 1,
        type: 'system',
        content: response.answer,
        timestamp: new Date(),
        answerResponse: response
      };

      setMessages(prev => [...prev, systemMessage]);

      if (response.citations?.length > 0) {
        showSuccess(`Found answer with ${response.citations.length} sources`);
      }

    } catch (error) {
      console.error('Search error:', error);

      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: `Search failed: ${error.message}`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
      showError(error.message);
    } finally {
      setIsLoading(false);
      // Refocus input for next query
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleNewChat = async () => {
    try {
      if (sessionId) {
        await clearAnswerSearchSession(sessionId);
      }

      // Create new session
      const session = await createAnswerSearchSession();
      setSessionId(session.session_id);

      // Clear messages and add welcome
      setMessages([{
        id: Date.now(),
        type: 'system',
        content: 'New chat started! Ask me about your articles and highlights.',
        timestamp: new Date()
      }]);

      showSuccess('New chat session created');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Failed to start new chat:', error);
      showError('Failed to start new chat');
    }
  };

  // Expose handleNewChat to parent via ref
  React.useImperativeHandle(ref, () => ({
    handleNewChat
  }));

  const handleArticleClick = (e, articleId) => {
    e.preventDefault();

    // Save search state to session storage for restoration
    const searchState = {
      searchMessages: messages,
      searchSessionId: sessionId
    };
    sessionStorage.setItem('searchModalState', JSON.stringify(searchState));

    // Navigate with search state preserved
    navigate(`/article/${articleId}`, {
      state: {
        fromSearch: true,
        searchMessages: messages,
        searchSessionId: sessionId,
        returnPath: location.pathname
      }
    });

    // Close modal after navigation
    if (onArticleClick) {
      onArticleClick();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render answer with markdown support
  const renderAnswer = (answer) => {
    if (!answer) return null;
    return (
      <div className="prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0">
        <ReactMarkdown>{answer}</ReactMarkdown>
      </div>
    );
  };

  // Render source cards
  const renderSources = (response) => {
    if (!response?.citations || response.citations.length === 0) return null;

    return (
      <div className="mt-4 space-y-2">
        <div className="text-xs font-medium text-secondary-text uppercase tracking-wide">
          Sources ({response.citations.length})
        </div>
        <div className="space-y-2">
          {response.citations.map((citation) => (
            <div
              key={citation.citation_number}
              id={`source-${citation.citation_number}`}
              className="bg-surface-alt/50 hover:bg-surface-hover p-3 rounded-lg border transition-all"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-medium">
                  {citation.citation_number}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {citation.source_type === 'highlight' ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200">
                        ✨ Highlight
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-surface-hover text-body-text">
                        📄 Article
                      </span>
                    )}
                  </div>
                  {citation.source_type === 'highlight' ? (
                    // For highlights: show excerpt prominently, link to article if available
                    <>
                      <p className="text-xs text-secondary-text mt-1 line-clamp-3">
                        &ldquo;{citation.excerpt}&rdquo;
                      </p>
                      {citation.article_id && citation.title && citation.title !== 'Untitled' && (
                        <Link
                          to={`/article/${citation.article_id}`}
                          onClick={(e) => handleArticleClick(e, citation.article_id)}
                          className="text-xs text-blue-600 hover:text-blue-800 mt-1 block"
                        >
                          from: {citation.title}
                        </Link>
                      )}
                    </>
                  ) : (
                    // For articles: show title prominently, excerpt below
                    <>
                      {citation.article_id ? (
                        <Link
                          to={`/article/${citation.article_id}`}
                          onClick={(e) => handleArticleClick(e, citation.article_id)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 block mt-1"
                        >
                          {citation.title || 'Untitled'}
                        </Link>
                      ) : (
                        <span className="text-sm font-medium text-heading block mt-1">
                          {citation.title || 'Untitled'}
                        </span>
                      )}
                      <p className="text-xs text-secondary-text mt-1 line-clamp-2">
                        {citation.excerpt}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Timing info */}
        <div className="text-xs text-tertiary-text mt-2">
          {response.search_query && (
            <div className="mb-1 text-secondary-text">
              Searched: &ldquo;{response.search_query}&rdquo;
            </div>
          )}
          Search: {response.search_time_ms}ms • Generation: {response.generation_time_ms}ms •
          Total: {response.total_time_ms}ms •
          Searched {response.highlights_searched} highlights, {response.articles_searched} articles
        </div>
      </div>
    );
  };

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div key={message.id} className="flex flex-col">
            <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-xs lg:max-w-2xl px-4 py-3 rounded-lg shadow-sm ${
                message.type === 'user'
                  ? 'bg-blue-500 text-white'
                  : message.type === 'error'
                  ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
                  : 'bg-surface-hover text-heading'
              }`}>
                {message.type === 'system' && message.answerResponse ? (
                  <div className="text-sm">
                    {renderAnswer(message.content)}
                  </div>
                ) : (
                  <p className="text-sm">{message.content}</p>
                )}
                <p className={`text-xs mt-2 ${
                  message.type === 'user' ? 'text-blue-100' : 'text-secondary-text'
                }`}>
                  {formatTime(message.timestamp)}
                </p>
              </div>
            </div>

            {/* Render sources if present */}
            {message.answerResponse && (
              <div className="ml-0 mt-2">
                {renderSources(message.answerResponse)}
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-surface-hover px-4 py-3 rounded-lg shadow-sm">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary-text"></div>
                <span className="text-sm text-secondary-text">Searching and generating answer...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t bg-surface p-4">
        <form onSubmit={handleSearch} className="flex gap-3 items-center">
          <textarea
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              // Auto-resize textarea
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={(e) => {
              // Submit on Enter, allow new line on Shift+Enter
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSearch(e);
              }
            }}
            placeholder="Ask about your articles and highlights... (Shift+Enter for new line)"
            className="flex-1 px-4 py-2.5 border border-theme-border-subtle rounded-lg bg-surface text-body-text placeholder-[color:var(--color-text-tertiary)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none overflow-hidden"
            disabled={isLoading}
            rows={1}
            style={{ minHeight: '44px', maxHeight: '120px' }}
          />
          <button
            type="submit"
            disabled={isLoading || !query.trim()}
            className="px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex-shrink-0 h-[44px]"
          >
            {isLoading ? 'Thinking...' : 'Ask'}
          </button>
        </form>
      </div>
    </div>
  );
});

ChatSearch.displayName = 'ChatSearch';

export default ChatSearch;
